import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { UserService } from '../user.service';
import { IFeatureFlags } from './featureflags';

/** Raw JSON model (matches the JSON file from API) */
interface RawServiceFlags {
	service: string;
	flags: RawFlag[];
}

interface RawFlag {
	id: string;
	service?: string | null;
	enabled: boolean;
	variants?: RawFlagVariant[] | null;
	description?: string;
	createdAt?: string;
	expiresAt?: string;
}

interface RawFlagVariant {
	id?: string | null; // account id
	enabled: boolean;
}

/** Internal normalized models */
export interface FlagVariant {
	accountId: string;
	enabled: boolean;
}

export interface Flag {
	enabled: boolean;
	variants: Map<string, FlagVariant> | null;
	description?: string;
	createdAt?: string;
	expiresAt?: string;
}

export interface ServiceFlags {
	service: string;
	flags: Map<string, Flag>;
}

/** FeatureFlagsManager - Loads and caches flags.json from the API - Provides IsFlagEnabled(flagId) to check flag or variant flag status */
@Injectable({
	providedIn: 'root',
})
export class FeatureFlagsService {
	private static _flags: Map<string, ServiceFlags> | null = null;

	public static get flags(): Map<string, ServiceFlags> | null {
		return this._flags;
	}

	constructor(private userService: UserService) {}

	/** Ensure flags are loaded exactly once (per process). */
	public static async ensureLoaded(): Promise<void> {
		try {
			const raw = await this.fetchFlags();
			// only set _flags if it hasn't been set,
			// since multiple ensureLoaded() calls could have been invoked.
			if (!this._flags) {
				this._flags = this.normalize(raw);
			}
		} catch (error: unknown) {
			// if for any reason fetchFlags throws,
			// we catch silently here so the app can continue safely.
			console.error(`FeatureFlags Service Error ${String(error)}`);
		}
	}

	public isFlagEnabled(flagId: IFeatureFlags): boolean {
		const flags = FeatureFlagsService.flags;
		if (!flags) return false;

		const serviceFlags = flags.get('ui');
		if (!serviceFlags) return false;

		const flag = serviceFlags.flags.get(normalizeKey(flagId));
		if (!flag) return false;

		const accountId = this.userService.account.id?.trim();
		if (accountId && flag.variants) {
			const variant = flag.variants.get(normalizeKey(accountId));
			if (variant) return variant.enabled;
		}

		return flag.enabled;
	}

	private static async fetchFlags(): Promise<RawServiceFlags[]> {
		const flagsUrl = `${environment.awsEnvUri}/flags/flags.json`;
		const res = await fetch(flagsUrl, {
			method: 'GET',
			headers: {
				Accept: 'application/json',
			},
		});

		if (!res.ok) {
			const body = await safeReadText(res);
			throw new Error(`Failed to fetch flags.json from '${flagsUrl}'. Status: ${res.status} ${res.statusText}. Body: ${body}`);
		}

		const json = await res.json();

		if (!Array.isArray(json)) {
			throw new TypeError(`Invalid flags.json format: expected array at root.`);
		}

		return json as RawServiceFlags[];
	}

	public static normalize(raw: RawServiceFlags[]): Map<string, ServiceFlags> {
		const services = new Map<string, ServiceFlags>();

		for (const svc of raw) {
			const serviceKey = normalizeKey(svc.service);
			const flagsArray = svc.flags ?? [];
			const flagsMap = new Map<string, Flag>();

			for (const flag of flagsArray) {
				const flagKey = normalizeKey(flag.id);
				const variantsArray = flag.variants ?? [];
				let variantsMap: Map<string, FlagVariant> | null = null;

				if (variantsArray.length > 0) {
					variantsMap = new Map<string, FlagVariant>();
					for (const variant of variantsArray) {
						const accountId = (variant.id ?? '').trim();
						if (!accountId) continue;
						variantsMap.set(normalizeKey(accountId), {
							accountId,
							enabled: !!variant.enabled,
						});
					}
					if (variantsMap.size === 0) {
						variantsMap = null;
					}
				}

				// If expiresAt is present and now > expiresAt, force enabled true
				let enabled = Boolean(flag.enabled);
				if (flag.expiresAt && Date.now() > Date.parse(flag.expiresAt)) {
					enabled = true;
				}
				flagsMap.set(flagKey, {
					enabled,
					variants: variantsMap,
					description: flag.description,
					createdAt: flag.createdAt,
					expiresAt: flag.expiresAt,
				});
			}

			services.set(serviceKey, {
				service: svc.service,
				flags: flagsMap,
			});
		}

		return services;
	}
}

/** Normalize keys to behave case-insensitively. Using toLocaleLowerCase keeps it safe for most identifiers. */
function normalizeKey(value: string): string {
	return (value ?? '').trim().toLocaleLowerCase();
}

async function safeReadText(res: Response): Promise<string> {
	try {
		return res.text();
	} catch {
		return '';
	}
}
